import { Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { MatDialog } from '@angular/material/dialog';
import { geoXplore } from 'src/environments/environment';
import { Router } from '@angular/router';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { SliderDataService } from 'src/app/shared/services/slider-data.service';
import { FormControl } from '@angular/forms';
import { ProjectLocation } from 'src/app/shared/models/location-interface.modal';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { MapProject } from 'src/app/shared/models/map-project.model';
import * as turf from '@turf/turf';
import { GeoXploreModalData } from 'src/app/shared/models/graph-modal-input.model';
import { GeoMapService } from './services/geo-map.service';
import { ModalContentComponent } from './components/modal-content/modal-content.component';

type OptionKey = 'soilCategories' | 'densities' | 'moistures';
type FilteredKeyMap = {
  soilCategories: 'filteredSoils';
  densities: 'filteredDensities';
  moistures: 'filteredMoistures';
};

@Component({
    selector: 'app-geo-map-container',
    templateUrl: './geo-map-container.component.html',
    styleUrls: ['./geo-map-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GeoMapContainerComponent implements OnInit {
  public map: mapboxgl.Map;
  public marker: mapboxgl.Marker[] = [];
  public style = 'mapbox://styles/mapbox/streets-v12';
  public mapData: any;
  public specificStateLatAndLong: ProjectLocation = {
    longitude: null,
    latitude: null,
    radius: 0,
    soilType: '',
    soilCategoryID: 0,
  };

  public markersList: any = [];
  public sliderValue = 50;
  selectedMaterialType = 'native-soil';
  @ViewChild('soilCtrlAutocompleteInput') soilCtrlAutocompleteInput: ElementRef<HTMLInputElement>;
  @ViewChild('densityCtrlAutocompleteInput', {
    static: false,
  })
    densityCtrlAutocompleteInput!: ElementRef<HTMLInputElement>;

  @ViewChild('moistureCtrlAutocompleteInput', {
    static: false,
  })
    moistureCtrlAutocompleteInput!: ElementRef<HTMLInputElement>;

  mixIdCtrl = new FormControl('');
  soilTypes: string[] = [];
  filteredSoils: string[] = [];
  densitySamples: Record<string, string[]> = {
  };

  moistureSamples: Record<string, string[]> = {
  };

  filteredProjects: MapProject[] = [];

  private selectionMarker?: mapboxgl.Marker;
  private readonly circleSourceId = 'selected-radius-source';
  private readonly circleFillLayerId = 'selected-radius-fill';
  private readonly circleOutlineLayerId = 'selected-radius-outline';
  globalSearch = true;

  soilCtrl = new FormControl<string | null>('');
  densityCtrl = new FormControl<string | null>('');
  moistureCtrl = new FormControl<string | null>('');

  soilCategories: string[] = [];

  densities: string[] = [];
  filteredDensities: string[] = [];

  moistures: string[] = [];
  filteredMoistures: string[] = [];

  selectedSoilCategory: string | null = null;
  selectedDensity: string | null = null;
  selectedMoisture: string | null = null;

  geocoder : MapboxGeocoder;

  allSoilObjects : any[] = [];

  constructor(
    private dialog: MatDialog,
    public router: Router,
    private geoMapService: GeoMapService,
    private sliderDataService: SliderDataService,
    private dialogService : DialogService,
  ) {}

  canUseSoil = false;
  canUseDensity = false;
  canUseMoisture = false;

  ngOnInit() {
    this.updateControlStates();
    this.globalSearch = false;
    this.initGeocoder();
  }

  initGeocoder() {
    mapboxgl as typeof mapboxgl;
    this.map = new mapboxgl.Map({
      accessToken: geoXplore.accessToken,
      container: 'map',
      style: this.style,
      center: [-98.5795, 39.8283],
      zoom: 2,
    });

    this.geocoder = new MapboxGeocoder({
      accessToken: geoXplore.accessToken,
      types: 'district, place, locality, neighborhood, address',
      placeholder: 'Enter county, city, or location...',
      mapboxgl,
    });

    const geocoderContainer = document.getElementById('geocoder');

    if (geocoderContainer) {
      geocoderContainer.innerHTML = '';
      this.geocoder.addTo('#geocoder');
    }

    this.geocoder.on('result', (e) => {
      const [long, lat] = e.result.center;
      this.specificStateLatAndLong.longitude = long;
      this.specificStateLatAndLong.latitude = lat;
      this.specificStateLatAndLong.radius = this.sliderValue;
      this.addOrUpdateMarker();
      this.drawRadiusCircle();
      this.zoomToState();
    });

    this.geocoder.on('clear', () => {
      this.specificStateLatAndLong.longitude = null;
      this.specificStateLatAndLong.latitude = null;
      this.clearMarkerAndCircle();
    });

    this.map.addControl(
      new MapboxGeocoder({
        accessToken: geoXplore.accessToken,
        mapboxgl,
      }),
    );
    setTimeout(() => {
      this.map.flyTo({
        center: [-98.5795, 39.8283],
        zoom: 3,
        speed: 0.5,
        curve: 0.8,
      });
    }, 1600);
    this.map.addControl(new mapboxgl.NavigationControl());
  }

  onArrowClick() {
    if (this.specificStateLatAndLong.latitude !== null && this.specificStateLatAndLong.longitude !== null) {
      this.onSearchClick();
    }
  }

  addMarkers() {
    if (this.marker) {
      this.marker.forEach((marker) => {
        marker.remove();
      });
    }
    this.filteredProjects.forEach((project: MapProject, index: number) => {
      if (project.Latitude && project.Longitude) {
        this.marker[index] = new mapboxgl.Marker({
          color: '#FF5733',
          offset: [0, -50 / 2],
        })
          .setLngLat([project.Longitude, project.Latitude])
          .addTo(this.map);

        this.marker[index].getElement().addEventListener('click', () => {
          this.openModal(project);
        });
      }
    });
  }

  public openModal(project: MapProject) {
    const modalData : GeoXploreModalData = {
      soilType: this.selectedSoilCategory,
      MapProject: project,
      materialType: this.selectedMaterialType,
      selectedDensity: this.selectedDensity,
      selectedMoisture: this.selectedMoisture,
      mixId: this.mixIdCtrl.value,
      radius: this.specificStateLatAndLong.radius,
      latitude: this.specificStateLatAndLong.latitude,
      longitude: this.specificStateLatAndLong.longitude,
    };

    this.dialog.open(ModalContentComponent, {
      data: modalData,
      disableClose: true,
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'full-screen-dialog',
      position: {
        top: '0', left: '0',
      },
    });
  }

  onSliderChange(event: Event): void {
    const { value } = event.target as HTMLInputElement;
    this.sliderValue = Number(value);
    this.sliderDataService.updateSliderValue(this.sliderValue);
    this.specificStateLatAndLong.radius = this.sliderValue;
    this.drawRadiusCircle();
  }

  public zoomToState() {
    setTimeout(() => {
      let zoom = 1;
      let lat = 0;
      let long = 0;
      if (this.specificStateLatAndLong.longitude !== null && this.specificStateLatAndLong.latitude !== null) {
        long = this.specificStateLatAndLong.longitude;
        lat = this.specificStateLatAndLong.latitude;
        zoom = 6.5;
      } else {
        long = -98.5795;
        lat = 39.8283;
        zoom = 3;
      }
      this.map.flyTo({
        center: [long, lat],
        zoom,
        speed: 0.5,
        curve: 0.8,
      });
    }, 200);
  }

  redirectToDasboard() {
    this.router.navigateByUrl('dashboard');
  }

  onSearchClick() {
    this.zoomToState();

    const request$ = this.selectedMaterialType === 'precast'
      ? this.geoMapService.getPrecastLocations({
        ...this.specificStateLatAndLong,
        mixId: this.mixIdCtrl.value?.trim(),
      })
      : this.geoMapService.getNativeSoilLocations(this.specificStateLatAndLong);

    request$.subscribe({
      next: (data) => {
        this.handleLocationResponse(data);
      },
    });
  }

  private addOrUpdateMarker() {
    if (this.specificStateLatAndLong.longitude !== null && this.specificStateLatAndLong.latitude !== null) {
      if (!this.selectionMarker) {
        this.selectionMarker = new mapboxgl.Marker({
          color: '#1E90FF',
        })
          .setLngLat([this.specificStateLatAndLong.longitude, this.specificStateLatAndLong.latitude])
          .addTo(this.map);
      } else {
        this.selectionMarker.setLngLat([this.specificStateLatAndLong.longitude, this.specificStateLatAndLong.latitude]);
      }
    }
  }

  private drawRadiusCircle() {
    if (this.specificStateLatAndLong.longitude !== null && this.specificStateLatAndLong.latitude !== null) {
      const circle = turf.circle([this.specificStateLatAndLong.longitude, this.specificStateLatAndLong.latitude], this.sliderValue, {
        units: 'miles',
      });

      const existing = this.map.getSource(this.circleSourceId) as mapboxgl.GeoJSONSource | undefined;
      if (existing) {
        existing.setData(circle);
        return;
      }

      this.map.addSource(this.circleSourceId, {
        type: 'geojson',
        data: circle,
      });

      this.map.addLayer({
        id: this.circleFillLayerId,
        type: 'fill',
        source: this.circleSourceId,
        paint: {
          'fill-color': '#1E90FF',
          'fill-opacity': 0.15,
        },
      });

      this.map.addLayer({
        id: this.circleOutlineLayerId,
        type: 'line',
        source: this.circleSourceId,
        paint: {
          'line-color': '#1E90FF',
          'line-width': 2,
        },
      });
    }
  }

  private clearMarkerAndCircle(): void {
    if (this.selectionMarker) {
      this.selectionMarker.remove();
      this.selectionMarker = undefined;
    }

    if (this.map.getLayer(this.circleFillLayerId)) {
      this.map.removeLayer(this.circleFillLayerId);
    }
    if (this.map.getLayer(this.circleOutlineLayerId)) {
      this.map.removeLayer(this.circleOutlineLayerId);
    }

    const src = this.map.getSource(this.circleSourceId) as mapboxgl.GeoJSONSource | undefined;
    if (src) {
      this.map.removeSource(this.circleSourceId);
    }
  }

  get hasValidLatLng(): boolean {
    const { latitude, longitude } = this.specificStateLatAndLong ?? {

    };
    if (latitude !== null && longitude !== null) {
      return Number.isFinite(latitude)
            && Number.isFinite(longitude)
            && latitude >= -90 && latitude <= 90
            && longitude >= -180 && longitude <= 180;
    }
    return false;
  }

  normalizeGuid(x: string | null | undefined): string {
    if (!x) return '';
    return x.trim().replace(/^\{|\}$/g, '').toLowerCase();
  }

  private setControlState(
    ctrl: FormControl<string | null>,
    canUse: boolean,
    selectedProp: 'selectedSoilCategory' | 'selectedDensity' | 'selectedMoisture',
  ): void {
    if (!canUse) {
      ctrl.disable({
        emitEvent: false,
      });
      if (ctrl.value !== '') {
        ctrl.setValue('', {
          emitEvent: false,
        });
      }
      (this as any)[selectedProp] = null;
    } else {
      ctrl.enable({
        emitEvent: false,
      });
    }
  }

  private updateControlStates(): void {
    const entries = [
      {
        ctrl: this.soilCtrl, canUse: this.canUseSoil, key: 'selectedSoilCategory' as const,
      },
      {
        ctrl: this.densityCtrl, canUse: this.canUseDensity, key: 'selectedDensity' as const,
      },
      {
        ctrl: this.moistureCtrl, canUse: this.canUseMoisture, key: 'selectedMoisture' as const,
      },
    ];

    entries.forEach(({ ctrl, canUse, key }) => {
      this.setControlState(ctrl, canUse, key);
    });
  }

  private recalcControlFlags(): void {
    this.canUseSoil = (this.soilCategories?.length ?? 0) > 0;
    this.canUseDensity = this.selectedSoilCategory !== null && (this.densities?.length ?? 0) > 0;
    this.canUseMoisture = this.selectedSoilCategory !== null && this.selectedDensity !== null && (this.moistures?.length ?? 0) > 0;
  }

  private filteredKeyMap: FilteredKeyMap = {
    soilCategories: 'filteredSoils',
    densities: 'filteredDensities',
    moistures: 'filteredMoistures',
  };

  private normalize(list: (string | null | undefined)[] | null | undefined): string[] {
    return (list ?? []).filter((x): x is string => typeof x === 'string' && x.length > 0);
  }

  private setOptions(key: OptionKey, list: (string | null | undefined)[]): void {
    const cleaned = this.normalize(list);
    this[key] = cleaned as any;
    const filteredKey = this.filteredKeyMap[key];
    (this as any)[filteredKey] = [...cleaned];
    this.recalcControlFlags();
    this.updateControlStates();
  }

  setSoilCategories(list: string[]): void {
    this.setOptions('soilCategories', list);
  }

  setDensities(list: (string | null)[]): void {
    this.setOptions('densities', list);
  }

  setMoistures(list: (string | null)[]): void {
    this.setOptions('moistures', list);
  }

  private filterOptions(
    inputRef: ElementRef<HTMLInputElement> | null | undefined,
    options: string[],
  ): string[] {
    const filterValue = inputRef?.nativeElement?.value?.toLowerCase() ?? '';
    return (options ?? []).filter((o) => o.toLowerCase().includes(filterValue));
  }

  filterSoilCategories(): void {
    this.filteredSoils = this.filterOptions(this.soilCtrlAutocompleteInput, this.soilCategories);
  }

  filterDensities(): void {
    this.filteredDensities = this.filterOptions(this.densityCtrlAutocompleteInput, this.densities);
  }

  filterMoistures(): void {
    this.filteredMoistures = this.filterOptions(this.moistureCtrlAutocompleteInput, this.moistures);
  }

  private validateSelection(
    ctrl: FormControl<string | null>,
    inputRef: ElementRef<HTMLInputElement> | null | undefined,
    options: readonly string[],
    allowAll = true,
  ): void {
    const val = inputRef?.nativeElement?.value?.trim() ?? '';
    const isValid = (allowAll && val === 'All') || new Set(options ?? []).has(val);
    if (!isValid) {
      ctrl.setValue('', {
        emitEvent: false,
      });
    }
  }

  validateSoilSelection(): void {
    this.validateSelection(this.soilCtrl, this.soilCtrlAutocompleteInput, this.soilCategories ?? []);
  }

  validateDensitySelection(): void {
    this.validateSelection(this.densityCtrl, this.densityCtrlAutocompleteInput, this.densities ?? []);
  }

  validateMoistureSelection(): void {
    this.validateSelection(this.moistureCtrl, this.moistureCtrlAutocompleteInput, this.moistures ?? []);
  }

  private allToNull = (v?: string | null) => ((v == null || v === '' || v.trim().toLowerCase() === 'all') ? null : v);

  private setLists(soils: string[] = [], densities: (string | null)[] = [], moistures: (string | null)[] = []) {
    this.setSoilCategories(soils);
    this.setDensities(densities);
    this.setMoistures(moistures);
  }

  private clearSelections(downstream: 'all' | 'density' | 'moisture' = 'all') {
    if (downstream === 'all' || downstream === 'density') {
      this.selectedDensity = null;
      this.densityCtrl.setValue('', {
        emitEvent: false,
      });
    }
    if (downstream === 'all' || downstream === 'moisture') {
      this.selectedMoisture = null;
      this.moistureCtrl.setValue('', {
        emitEvent: false,
      });
    }
  }

  private getRecords(soilCategory?: string | null, density?: string | null, moisture?: string | null): any[] {
    const normalizedSoilCategory = this.allToNull(soilCategory);
    const normalizedDensity = this.allToNull(density);
    const normalizedMoisture = this.allToNull(moisture);
    if (!this.mapData) return [];
    if (normalizedSoilCategory && normalizedDensity && normalizedMoisture) {
      return this.mapData?.categories?.[normalizedSoilCategory]?.densities?.[normalizedDensity]?.moistures?.[normalizedMoisture]?.coordinates ?? [];
    }
    if (normalizedSoilCategory && normalizedDensity) {
      return this.mapData?.categories?.[normalizedSoilCategory]?.densities?.[normalizedDensity]?.coordinates ?? [];
    }
    if (normalizedSoilCategory) {
      return this.mapData?.categories?.[normalizedSoilCategory]?.coordinates ?? [];
    }
    return this.mapData?.coordinates ?? [];
  }

  private refreshMarkersAndUi() {
    this.addMarkers();
    this.recalcControlFlags();
    this.updateControlStates();
  }

  private handleLocationResponse(data: any): void {
    this.setLists([], [], []);
    if (data.coordinates.length === 0) {
      this.dialogService.open({
        message: 'No Data Found',
        title: 'Error',
      });
      return;
    }
    if ('message' in data) {
      this.dialogService.open({
        message: data.message,
      });
      return;
    }
    this.mapData = data;

    this.filteredProjects = this.getRecords();
    this.addMarkers();

    this.setSoilCategories(data?.categoryrecords.map((categoryRecord: { Category: any; }) => categoryRecord.Category) ?? []);
  }

  onSearchByLocationChange(checked: boolean) {
    this.geocoder.clear();
    this.setLists([], [], []);
    this.filteredProjects = [];
    this.addMarkers();
    this.clearMarkerAndCircle();

    Object.assign(this.specificStateLatAndLong, {
      latitude: null, longitude: null, radius: 50, soilType: '',
    });
    this.sliderValue = 50;

    if (checked) {
      this.geoMapService.getNativeSoilTypes().subscribe({
        next: (soils: any[]) => {
          this.allSoilObjects = soils;
          const soilStrings = soils.map((soil) => soil.Category);
          this.setSoilCategories(soilStrings);
        },
      });
    } else {
      this.specificStateLatAndLong.soilCategoryID = 0;
    }
  }

  loadDensitiesForSoil(soil: string) {
    this.selectedSoilCategory = this.allToNull(soil);
    if (this.selectedSoilCategory) {
      const dens = this.mapData?.categories?.[this.selectedSoilCategory]?.density_ranges ?? [];
      const densStringList = dens.map((den: any[]) => `${den[0]}-${den[1]}`);
      this.setDensities(densStringList);
    } else {
      this.setDensities([]);
      this.setMoistures([]);
    }
  }

  loadMoisturesForSelection(soil: string | null, density: string | null) {
    const normalizedSoil = this.allToNull(soil);
    const normalizedDensity = this.allToNull(density);
    const list = (normalizedSoil && normalizedDensity)
      ? (this.mapData?.categories?.[normalizedSoil]?.densities?.[normalizedDensity]?.moisture_ranges ?? []) : [];
    const moistureStringList = list.map((moisture: any[]) => `${moisture[0]}-${moisture[1]}`);
    this.setMoistures(moistureStringList);
  }

  onSoilSelected(): void {
    this.selectedSoilCategory = this.allToNull(this.soilCtrl?.value as string);
    this.clearSelections('all');

    if (!this.globalSearch) {
      if (this.selectedSoilCategory) {
        this.loadDensitiesForSoil(this.selectedSoilCategory);
        this.filteredProjects = this.getRecords(this.selectedSoilCategory);
      } else {
        this.filteredProjects = this.getRecords();
        this.setDensities([]);
        this.setMoistures([]);
      }
      this.refreshMarkersAndUi();
      return;
    }

    if (this.selectedSoilCategory) {
      this.specificStateLatAndLong.soilType = this.selectedSoilCategory;
      this.specificStateLatAndLong.soilCategoryID = this.allSoilObjects.find((soilObject) => soilObject.Category === this.selectedSoilCategory).ID;
      this.zoomToState();
      this.geoMapService.getNativeSoilLocations(this.specificStateLatAndLong).subscribe({
        next: (data: any) => {
          this.mapData = data;
          if (this.selectedSoilCategory) {
            this.loadDensitiesForSoil(this.selectedSoilCategory);
            this.filteredProjects = this.getRecords(this.selectedSoilCategory);
            this.refreshMarkersAndUi();
          }
        },
      });
    } else {
      this.filteredProjects = [];
      this.addMarkers();
      this.setDensities([]);
      this.setMoistures([]);
      this.recalcControlFlags();
      this.updateControlStates();
    }
  }

  onDensitySelected(): void {
    this.selectedDensity = this.allToNull(this.densityCtrl.value as string);
    this.clearSelections('moisture');

    if (this.selectedSoilCategory && this.selectedDensity) {
      this.loadMoisturesForSelection(this.selectedSoilCategory, this.selectedDensity);
      this.filteredProjects = this.getRecords(this.selectedSoilCategory, this.selectedDensity);
    } else if (this.selectedSoilCategory) {
      this.filteredProjects = this.getRecords(this.selectedSoilCategory);
      this.setMoistures([]);
    }
    this.refreshMarkersAndUi();
  }

  onMoistureSelected(): void {
    this.selectedMoisture = this.allToNull(this.moistureCtrl.value as string);

    if (this.selectedSoilCategory && this.selectedDensity && this.selectedMoisture) {
      this.filteredProjects = this.getRecords(this.selectedSoilCategory, this.selectedDensity, this.selectedMoisture);
    } else if (this.selectedSoilCategory && this.selectedDensity) {
      this.filteredProjects = this.getRecords(this.selectedSoilCategory, this.selectedDensity);
    }
    this.refreshMarkersAndUi();
  }
}
